import * as preact from "preact";
import PropTypes from "prop-types";

import MultiSelectableListViewItem from "./MultiSelectableListViewItem";
import noderef from "../../foundation/utils/noderef";
import classname from "../../foundation/utils/classname";
import { modifierKeysUsed } from "../../foundation/utils/keyboard";

import "./style.css";

const SELECT_ALL_VALUE = "*";

class MultiSelectableListView extends preact.Component {
    state = {
        searchFilter: null,
        activeItemIndex: null,
        selectedItems: this.props.selectedItems,
        isSearchInputFocused: false
    }

    get itemsToRender() {
        const items = preact.toChildArray(this.props.children);
        const { searchFilter } = this.state;
        let computedItems = items;

        if (searchFilter) {
            computedItems = items.filter(({ props }) => props.label.toLowerCase().includes(searchFilter.trim().toLowerCase()));
        }

        return computedItems;
    }

    get valuesArrayFromItemsToRender() {
        return this.itemsToRender.map(({ props }) => props.value);
    }

    get areAllItemsToRenderSelected() {
        return this.valuesArrayFromItemsToRender.every(item => this.state.selectedItems.includes(item));
    }

    handleSearchInput = event => {
        this.setState({
            searchFilter: event.target.value
        });
    }

    handleSearchInputFocus = () => {
        this.setState({
            isSearchInputFocused: true
        });
    }

    handleSearchInputBlur = () => {
        this.setState({
            isSearchInputFocused: false
        });
    }

    handleItemClick = itemValue => {
        const { onChange } = this.props;
        const { selectedItems } = this.state;
        const itemIndex = selectedItems.indexOf(itemValue);
        let newSelectedItems = [...selectedItems];

        /*
            There are a couple of cases in here;
            1. If the selected item exists in selectedItems then remove it from selectedItems.
            2. If the selected item doesn't exist in selectedItems;
               2.1. If selected item is equal to SELECT_ALL_VALUE;
                    If all itemsToRender are selected then remove all itemsToRender from selectedItems.
                    If some or none of itemsToRender are selected then add all itemsToRender to selectedItems.
                    - selectedItems could be assigned to [] or itemsToRender for above cases but user might filter options,
                      in that case we don't want to lose already selected items.
               2.2. If selected item is not equal to SELECT_ALL_VALUE add selected item to selectedItems.
        */
        if (itemIndex === -1) {
            if (itemValue === SELECT_ALL_VALUE) {
                if (this.areAllItemsToRenderSelected) {
                    newSelectedItems = newSelectedItems.filter(item => !this.valuesArrayFromItemsToRender.includes(item));
                } else {
                    newSelectedItems = [...newSelectedItems, ...this.valuesArrayFromItemsToRender];
                    newSelectedItems = Array.from(new Set(newSelectedItems)); // Remove duplicates
                }
            } else {
                newSelectedItems.push(itemValue);
            }
        } else {
            newSelectedItems.splice(itemIndex, 1);
        }

        this.setState({
            selectedItems: newSelectedItems
        });
        onChange(newSelectedItems);
    }

    handleItemHover = itemValue => {
        if (this.state.isSearchInputFocused) {
            return;
        }

        const itemIndex = this.valuesArrayFromItemsToRender.indexOf(itemValue);

        this.setState({
            activeItemIndex: itemIndex + 1 // +1 for the select all option
        });
    }

    handleKeyDown = event => {
        const { activeItemIndex, isSearchInputFocused } = this.state;

        if (!this.itemsToRender.length || modifierKeysUsed(event)) {
            return;
        }

        if ([Zeplin.KEY_CODE.ArrowUp, Zeplin.KEY_CODE.ArrowDown].includes(event.keyCode)) {
            let newActiveItemIndex;

            event.preventDefault();

            if (activeItemIndex === null) {
                newActiveItemIndex = 0;
            } else if (event.keyCode === Zeplin.KEY_CODE.ArrowUp) {
                newActiveItemIndex = activeItemIndex > 0 ? activeItemIndex - 1 : this.itemsToRender.length;
            } else if (event.keyCode === Zeplin.KEY_CODE.ArrowDown) {
                newActiveItemIndex = activeItemIndex < this.itemsToRender.length ? activeItemIndex + 1 : 0;
            }

            this.setState({
                activeItemIndex: newActiveItemIndex
            });
        } else if (event.keyCode === Zeplin.KEY_CODE.ENTER || (event.keyCode === Zeplin.KEY_CODE.SPACE && !isSearchInputFocused)) {
            event.preventDefault();

            if (activeItemIndex === 0) {
                this.handleItemClick(SELECT_ALL_VALUE);
            } else {
                this.handleItemClick(this.valuesArrayFromItemsToRender[activeItemIndex - 1]); // -1 for the select all option
            }
        }
    }

    componentDidMount() {
        this.nodeRefs.input.focus();
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentDidUpdate(_, prevState) {
        const { onSearchFilterChange } = this.props;

        if (this.state.searchFilter !== prevState.searchFilter && onSearchFilterChange) {
            onSearchFilterChange();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    renderItems() {
        const { searchFilter, selectedItems, activeItemIndex } = this.state;
        let itemsToRender = this.itemsToRender;

        if (itemsToRender.length) {
            itemsToRender = [
                <MultiSelectableListViewItem value={SELECT_ALL_VALUE} label={searchFilter && searchFilter.trim() ? "Select all search results" : "Select all"}/>,
                ...itemsToRender
            ];
        }

        return itemsToRender.map((child, index) => {
            const { value, label, subLabel, description } = child.props;
            const isSelected = (
                selectedItems.includes(value) ||
                (value === SELECT_ALL_VALUE && this.areAllItemsToRenderSelected)
            );

            return preact.cloneElement(child, {
                key: value,
                value,
                label,
                subLabel,
                description,
                isSelected,
                isActive: activeItemIndex === index,
                onClick: this.handleItemClick,
                onHover: this.handleItemHover
            });
        });
    }

    render() {
        const { searchFilter } = this.state;
        const { class: cssClass } = this.props;

        return (
            <div class={classname("multiSelectableListView noSelect", cssClass)}>
                <div class="searchInputContainer">
                    <input
                        ref={noderef(this, "input")}
                        class="ellipsis"
                        value={searchFilter}
                        placeholder="Search…"
                        onInput={this.handleSearchInput}
                        onFocus={this.handleSearchInputFocus}
                        onBlur={this.handleSearchInputBlur}/>
                    {!this.itemsToRender.length && <span>¯\_(ツ)_/¯</span>}
                </div>
                {Boolean(this.itemsToRender.length) && <div class="list">{this.renderItems()}</div>}
            </div>
        );
    }
}

MultiSelectableListView.propTypes = {
    selectedItems: PropTypes.array.isRequired,
    children: PropTypes.oneOfType([PropTypes.instanceOf(MultiSelectableListViewItem), PropTypes.array]).isRequired,
    onChange: PropTypes.func.isRequired,
    onSearchFilterChange: PropTypes.func,
    class: PropTypes.string
};

MultiSelectableListView.Item = MultiSelectableListViewItem;
export default MultiSelectableListView;
export { SELECT_ALL_VALUE };
